import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, ArrowLeft, Sparkles, Crown, Heart, Zap, Star, Shield, Video, TrendingUp } from 'lucide-react';
import { BraintreeCheckout } from '@/components/BraintreeCheckout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_TITLES = { standard: 'Standard', premium: 'Premium', private_match: 'Private Match' };
const PLAN_PRICE_LABELS = { standard: '€9.99/month', premium: '€19/month', private_match: '€49/month' };
const ADDON_TITLES = {
  live_match: 'Live Match Event', profile_boost: 'Profile Boost', super_likes: 'Super Likes',
  background_check: 'Background Check', video_date: 'Guided Video Date',
};
const ADDON_PRICE_LABELS = {
  live_match: '€19', profile_boost: '€9.99', super_likes: '€4.99', background_check: '€29', video_date: '€14.99',
};
const ADDON_AMOUNTS = {
  live_match: '19.00', profile_boost: '9.99', super_likes: '4.99', background_check: '29.00', video_date: '14.99',
};

const NewPricingPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const welcome = searchParams.get('welcome') === '1';
  const [loading, setLoading] = useState(false);
  const [addonLoading, setAddonLoading] = useState('');
  const [checkout, setCheckout] = useState(null); // {mode, planType|addonType, title, priceLabel}

  const handleSubscribe = (planType) => {
    if (!user) { navigate('/auth'); return; }
    setCheckout({ mode: 'subscription', planType, title: `${PLAN_TITLES[planType]} Plan`, priceLabel: PLAN_PRICE_LABELS[planType] });
  };

  const handleAddonPurchase = (addonType) => {
    if (!user) { navigate('/auth'); return; }
    setCheckout({ mode: 'addon', addonType, title: ADDON_TITLES[addonType], priceLabel: ADDON_PRICE_LABELS[addonType], amount: ADDON_AMOUNTS[addonType] });
  };

  const onCheckoutSuccess = () => {
    setCheckout(null);
    navigate('/profile');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {user && (
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 text-rose-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        )}

        <div className="text-center mb-16">
          {welcome && (
            <div className="max-w-2xl mx-auto mb-8 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-rose-50 px-6 py-4 flex items-center justify-center gap-3" data-testid="welcome-banner">
              <Sparkles className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm sm:text-base text-rose-900">Your profile is ready! 🎉 Choose a plan to supercharge your matches — or continue on Free.</p>
            </div>
          )}
          <h1 className="text-5xl md:text-6xl font-bold text-rose-900 mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            {welcome ? 'Pick Your Plan' : 'Choose Your Plan'}
          </h1>
          <p className="text-xl text-gray-600">Find the perfect plan for your journey to lasting love</p>
          {welcome && (
            <Button data-testid="continue-free-btn" variant="ghost" className="mt-4 text-rose-700" onClick={() => navigate('/dashboard')}>
              Continue on Free for now <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          )}
        </div>

        {/* Subscription Plans */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {/* Free Plan */}
          <Card className="shadow-xl border-gray-200">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-6 h-6 text-gray-400" />
                <CardTitle className="text-2xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>Free</CardTitle>
              </div>
              <CardDescription className="text-3xl font-bold text-gray-900 mt-2">€0<span className="text-base font-normal text-gray-500">/month</span></CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                <Feature text="5 daily matches" />
                <Feature text="Basic messaging" />
                <Feature text="Profile creation" />
                <Feature text="Compatibility scores" />
                <Feature text="Weekly reflections" />
              </ul>
              <Button
                data-testid="free-plan-btn"
                variant="outline"
                className="w-full border-gray-300"
                onClick={() => user ? navigate('/dashboard') : navigate('/auth')}
              >
                {user ? 'Current Plan' : 'Get Started'}
              </Button>
            </CardContent>
          </Card>

          {/* Standard Plan */}
          <Card className="shadow-xl border-blue-200">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-6 h-6 text-blue-500" />
                <CardTitle className="text-2xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>Standard</CardTitle>
              </div>
              <CardDescription className="text-3xl font-bold text-blue-900 mt-2">€9.99<span className="text-base font-normal text-gray-500">/month</span></CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                <Feature text="15 daily matches" premium />
                <Feature text="Advanced messaging" premium />
                <Feature text="AI conversation starters" premium />
                <Feature text="Profile visibility boost" premium />
                <Feature text="See who liked you" premium />
                <Feature text="Verification badge" premium />
              </ul>
              <Button
                data-testid="standard-plan-btn"
                className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                onClick={() => handleSubscribe('standard')}
                disabled={loading}
              >
                <Star className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Upgrade to Standard'}
              </Button>
            </CardContent>
          </Card>

          {/* Premium Plan */}
          <Card className="shadow-2xl border-rose-400 border-2 relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-rose-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
              Most Popular
            </div>
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-6 h-6 text-rose-500" />
                <CardTitle className="text-2xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>Premium</CardTitle>
              </div>
              <CardDescription className="text-3xl font-bold text-rose-900 mt-2">€19<span className="text-base font-normal text-gray-500">/month</span></CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                <Feature text="Unlimited daily matches" premium />
                <Feature text="Browse & search all users" premium />
                <Feature text="AI date ideas" premium />
                <Feature text="Red-flag detection" premium />
                <Feature text="Advanced filters" premium />
                <Feature text="Priority support" premium />
                <Feature text="Read receipts" premium />
              </ul>
              <Button
                data-testid="premium-plan-btn"
                className="w-full bg-rose-500 hover:bg-rose-600"
                onClick={() => handleSubscribe('premium')}
                disabled={loading}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Upgrade to Premium'}
              </Button>
            </CardContent>
          </Card>

          {/* Private Match Plan */}
          <Card className="shadow-2xl border-amber-400 border-2 bg-gradient-to-br from-amber-50 to-rose-50">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-rose-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
              VIP Concierge
            </div>
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-6 h-6 text-amber-600" />
                <CardTitle className="text-2xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>Private Match</CardTitle>
              </div>
              <CardDescription className="text-3xl font-bold text-amber-900 mt-2">€49<span className="text-base font-normal text-gray-500">/month</span></CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                <Feature text="1-on-1 matchmaker interview" premium />
                <Feature text="Handpicked matches" premium />
                <Feature text="Direct messaging anyone" premium />
                <Feature text="3-way conversation with host" premium />
                <Feature text="Profile optimization" premium />
                <Feature text="Date coaching sessions" premium />
                <Feature text="Background verification" premium />
                <Feature text="Everything in Premium" premium />
              </ul>
              <Button
                data-testid="private-match-plan-btn"
                className="w-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white"
                onClick={() => handleSubscribe('private_match')}
                disabled={loading}
              >
                <Crown className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Go VIP'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Addons Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-rose-900 mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              Boost Your Experience
            </h2>
            <p className="text-lg text-gray-600">One-time purchases to enhance your dating journey</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Live Match Addon */}
            <Card className="shadow-lg border-rose-200 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-6 h-6 text-rose-500" />
                  <CardTitle className="text-xl">Live Match Event</CardTitle>
                </div>
                <CardDescription className="text-2xl font-bold text-rose-900">€19</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Join virtual speed dating events with curated participants</p>
                <ul className="space-y-2 mb-6">
                  <Feature text="1 event access" premium />
                  <Feature text="Real-time video matching" premium />
                  <Feature text="Professional host" premium />
                </ul>
                <Button
                  data-testid="live-match-addon-btn"
                  className="w-full bg-rose-500 hover:bg-rose-600"
                  onClick={() => handleAddonPurchase('live_match')}
                  disabled={addonLoading === 'live_match'}
                >
                  {addonLoading === 'live_match' ? 'Processing...' : 'Purchase'}
                </Button>
              </CardContent>
            </Card>

            {/* Profile Boost */}
            <Card className="shadow-lg border-blue-200 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-6 h-6 text-blue-500" />
                  <CardTitle className="text-xl">Profile Boost</CardTitle>
                </div>
                <CardDescription className="text-2xl font-bold text-blue-900">€9.99</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Be featured at the top for 24 hours</p>
                <ul className="space-y-2 mb-6">
                  <Feature text="10x more visibility" premium />
                  <Feature text="Featured badge" premium />
                  <Feature text="24-hour duration" premium />
                </ul>
                <Button
                  data-testid="profile-boost-addon-btn"
                  variant="outline"
                  className="w-full border-blue-300"
                  onClick={() => handleAddonPurchase('profile_boost')}
                  disabled={addonLoading === 'profile_boost'}
                >
                  {addonLoading === 'profile_boost' ? 'Processing...' : 'Purchase'}
                </Button>
              </CardContent>
            </Card>

            {/* Super Likes */}
            <Card className="shadow-lg border-purple-200 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="w-6 h-6 text-purple-500 fill-purple-500" />
                  <CardTitle className="text-xl">Super Likes</CardTitle>
                </div>
                <CardDescription className="text-2xl font-bold text-purple-900">€4.99</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Stand out with special likes</p>
                <ul className="space-y-2 mb-6">
                  <Feature text="5 super likes" premium />
                  <Feature text="Instant notifications" premium />
                  <Feature text="Priority attention" premium />
                </ul>
                <Button
                  data-testid="super-likes-addon-btn"
                  variant="outline"
                  className="w-full border-purple-300"
                  onClick={() => handleAddonPurchase('super_likes')}
                  disabled={addonLoading === 'super_likes'}
                >
                  {addonLoading === 'super_likes' ? 'Processing...' : 'Purchase'}
                </Button>
              </CardContent>
            </Card>

            {/* Background Check */}
            <Card className="shadow-lg border-green-200 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-6 h-6 text-green-500" />
                  <CardTitle className="text-xl">Background Check</CardTitle>
                </div>
                <CardDescription className="text-2xl font-bold text-green-900">€29</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Professional identity verification</p>
                <ul className="space-y-2 mb-6">
                  <Feature text="Identity verification" premium />
                  <Feature text="Photo verification" premium />
                  <Feature text="Verified badge" premium />
                </ul>
                <Button
                  data-testid="background-check-addon-btn"
                  variant="outline"
                  className="w-full border-green-300"
                  onClick={() => handleAddonPurchase('background_check')}
                  disabled={addonLoading === 'background_check'}
                >
                  {addonLoading === 'background_check' ? 'Processing...' : 'Purchase'}
                </Button>
              </CardContent>
            </Card>

            {/* Video Date */}
            <Card className="shadow-lg border-indigo-200 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Video className="w-6 h-6 text-indigo-500" />
                  <CardTitle className="text-xl">Guided Video Date</CardTitle>
                </div>
                <CardDescription className="text-2xl font-bold text-indigo-900">€14.99</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Expert-guided virtual date session</p>
                <ul className="space-y-2 mb-6">
                  <Feature text="Professional host" premium />
                  <Feature text="Ice-breaker activities" premium />
                  <Feature text="60-minute session" premium />
                </ul>
                <Button
                  data-testid="video-date-addon-btn"
                  variant="outline"
                  className="w-full border-indigo-300"
                  onClick={() => handleAddonPurchase('video_date')}
                  disabled={addonLoading === 'video_date'}
                >
                  {addonLoading === 'video_date' ? 'Processing...' : 'Purchase'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      {checkout && (
        <BraintreeCheckout
          open={!!checkout}
          onOpenChange={(v) => { if (!v) setCheckout(null); }}
          mode={checkout.mode}
          planType={checkout.planType}
          addonType={checkout.addonType}
          title={checkout.title}
          priceLabel={checkout.priceLabel}
          amount={checkout.amount}
          onSuccess={onCheckoutSuccess}
        />
      )}
    </div>
  );
};

const Feature = ({ text, premium }) => (
  <li className="flex items-center gap-2">
    <Check className={`w-5 h-5 flex-shrink-0 ${premium ? 'text-rose-500' : 'text-gray-400'}`} />
    <span className={premium ? 'text-gray-900' : 'text-gray-600'}>{text}</span>
  </li>
);

export default NewPricingPage;
